import Decoders from '../Models/decoders';
import Questions from '../Models/questions';
import {ModuleResult} from '../Engine/session';
import {ModuleVerdict, summariseVerdicts} from '../Engine/verdictSummary';
import {AppLanguageCode, getCopy} from './I18n';
import Storage from './Storage';

export type {ModuleVerdict} from '../Engine/verdictSummary';

export const ACTIVE_ASSESSMENT_STORAGE_KEY = 'adapt-mh.assessment.active';
export const COMPLETED_ASSESSMENTS_STORAGE_KEY = 'adapt-mh.assessment.completed';

export type AssessmentMode = 'self-check' | 'guided' | 'research' | 'debug';
export type AssessmentStatus = 'active' | 'completed';
export type SharingConsent = 'private' | 'share-summary' | 'research-only';
export type RiskLevel = 'none' | 'urgent';
export type SeverityLevel =
  | 'doing_okay'
  | 'some_stress'
  | 'difficult_time'
  | 'extra_support'
  | 'urgent';

export type AssessmentLanguageCode = AppLanguageCode;

export type AssessmentLanguage = {
  code: AssessmentLanguageCode;
  label: string;
  helper: string;
};

export type AssessmentAnswer = {
  questionId: string;
  questionTitle: string;
  answerValue: string;
  answerLabel: string;
  answeredAt: string;
};

export type RiskFlag = {
  level: RiskLevel;
  triggered: boolean;
  questionId?: string;
  message: string;
};

export type AssessmentResultSummary = {
  sessionId: string;
  completedAt: string;
  severityLevel: SeverityLevel;
  headline: string;
  explanation: string;
  suggestedAction: string;
  maxProbability: number;
  topConcernLabel: string;
  predictions: Record<string, number>;
  // Per-module DISC-5 verdicts (structural-entropy engine). Optional so legacy
  // probability-based summaries remain valid.
  moduleVerdicts?: ModuleVerdict[];
  riskFlag: RiskFlag;
};

export type ProviderActionType =
  | 'none_today'
  | 'referral_suggested'
  | 'follow_up_needed'
  | 'urgent_escalation';

export type ReferralStatus = 'none' | 'suggested' | 'follow_up' | 'urgent_escalation';

export type ProviderActionRecord = {
  type: ProviderActionType;
  label: string;
  actedAt: string;
  note?: string;
};

export type FollowUpRecord = {
  id: string;
  date: string;
  note: string;
  status: 'planned' | 'completed';
};

export type AssessmentTrendDirection = 'first' | 'higher' | 'lower' | 'similar';

export type AssessmentTrend = {
  direction: AssessmentTrendDirection;
  label: string;
  description: string;
  difference: number;
  previousCompletedAt?: string;
  deterioration: boolean;
};

export type AssessmentSessionSnapshot = {
  sessionId: string;
  mode: AssessmentMode;
  language: AssessmentLanguageCode;
  startedByProvider?: boolean;
  sharingConsent?: SharingConsent;
  participantAlias?: string;
  status: AssessmentStatus;
  answers: AssessmentAnswer[];
  answeredQuestions: string[];
  selectedQuestion: string | 'notSelected';
  predictions?: Record<string, number>;
  riskFlag: RiskFlag;
  resultSummary?: AssessmentResultSummary;
  createdAt: string;
  updatedAt: string;
};

export type ProviderAssessmentRecord = AssessmentSessionSnapshot & {
  status: 'completed';
  resultSummary: AssessmentResultSummary;
  providerAction?: ProviderActionRecord;
  referralStatus?: ReferralStatus;
  followUpDate?: string;
  providerNotes?: string;
  followUps?: FollowUpRecord[];
};

export type SupportOption = {
  title: string;
  description: string;
  urgent?: boolean;
};

export const ASSESSMENT_LANGUAGES: AssessmentLanguage[] = [
  {
    code: 'en',
    label: 'English',
    helper: 'Available',
  },
  {
    code: 'sw',
    label: 'Swahili',
    helper: 'Prototype translation',
  },
  {
    code: 'ts',
    label: 'XiTsonga',
    helper: 'Prototype translation',
  },
  {
    code: 'fr',
    label: 'Français',
    helper: 'Prototype translation',
  },
];

export const SUPPORT_OPTIONS: SupportOption[] = [
  {
    title: 'Talk to someone you trust',
    description:
      'A trusted adult, counsellor, teacher, nurse, or youth worker can help you decide what to do next.',
  },
  {
    title: 'Visit a local support service',
    description:
      'Use the local clinic, school counsellor, youth organisation, or community support pathway approved for your site.',
  },
  {
    title: 'Get urgent help now',
    description:
      'If you feel unsafe or may hurt yourself, please speak to a trusted adult, counsellor, nurse, or emergency support service now.',
    urgent: true,
  },
];

export const createSessionId = (): string => {
  return `adapt-mh-${Date.now()}-${Math.round(Math.random() * 100000)}`;
};

export const createEmptyRiskFlag = (): RiskFlag => ({
  level: 'none',
  triggered: false,
  message: getCopy('en').risk.none,
});

type InitialSessionOptions = {
  mode?: AssessmentMode;
  startedByProvider?: boolean;
  sharingConsent?: SharingConsent;
  participantAlias?: string;
};

export const createInitialSession = (
  language: AssessmentLanguageCode = 'en',
  options: InitialSessionOptions = {},
): AssessmentSessionSnapshot => {
  const now = new Date().toISOString();

  return {
    sessionId: createSessionId(),
    mode: options.mode || 'self-check',
    language,
    startedByProvider: options.startedByProvider,
    sharingConsent: options.sharingConsent,
    participantAlias: options.participantAlias,
    status: 'active',
    answers: [],
    answeredQuestions: [],
    selectedQuestion: 'notSelected',
    riskFlag: createEmptyRiskFlag(),
    createdAt: now,
    updatedAt: now,
  };
};

export const isRiskAnswer = (questionId: string, answerValue: string): boolean => {
  return questionId.startsWith('a_suicide') && answerValue === '1';
};

export const detectRiskFlag = (questionId: string, answerValue: string): RiskFlag => {
  if (!isRiskAnswer(questionId, answerValue)) {
    return createEmptyRiskFlag();
  }

  return {
    level: 'urgent',
    triggered: true,
    questionId,
    message: getCopy('en').risk.urgent,
  };
};

export const mergeRiskFlags = (current: RiskFlag, next: RiskFlag): RiskFlag => {
  return current.triggered ? current : next;
};

export const getAnswerLabel = (questionId: keyof typeof Questions, answerValue: string): string => {
  return Questions[questionId]?.choices?.[answerValue] || answerValue;
};

export const buildAssessmentAnswer = (
  questionId: keyof typeof Questions,
  answerValue: string,
): AssessmentAnswer => ({
  questionId,
  questionTitle: Questions[questionId]?.title || questionId,
  answerValue,
  answerLabel: getAnswerLabel(questionId, answerValue),
  answeredAt: new Date().toISOString(),
});

export const upsertAnswer = (
  answers: AssessmentAnswer[],
  nextAnswer: AssessmentAnswer,
): AssessmentAnswer[] => {
  const filteredAnswers = answers.filter((answer) => answer.questionId !== nextAnswer.questionId);
  return [...filteredAnswers, nextAnswer];
};

const buildNonUrgentSummary = (
  maxProbability: number,
  language: AssessmentLanguageCode,
): Omit<
  AssessmentResultSummary,
  'sessionId' | 'completedAt' | 'maxProbability' | 'topConcernLabel' | 'predictions' | 'riskFlag'
> => {
  const copy = getCopy(language);

  if (maxProbability < 0.35) {
    return {
      severityLevel: 'doing_okay',
      ...copy.resultSummaries.doing_okay,
    };
  }

  if (maxProbability < 0.55) {
    return {
      severityLevel: 'some_stress',
      ...copy.resultSummaries.some_stress,
    };
  }

  if (maxProbability < 0.75) {
    return {
      severityLevel: 'difficult_time',
      ...copy.resultSummaries.difficult_time,
    };
  }

  return {
    severityLevel: 'extra_support',
    ...copy.resultSummaries.extra_support,
  };
};

export const buildResultSummary = (
  sessionId: string,
  predictions: Record<string, number> | undefined,
  riskFlag: RiskFlag,
  language: AssessmentLanguageCode = 'en',
): AssessmentResultSummary => {
  const copy = getCopy(language);
  const safePredictions = predictions || {};
  const predictionEntries = Object.entries(safePredictions);
  const topPrediction = predictionEntries.reduce<[string, number]>(
    (best, candidate) => (candidate[1] > best[1] ? candidate : best),
    ['', 0],
  );
  const topConcernLabel = Decoders[topPrediction[0] as keyof typeof Decoders] || 'wellbeing';
  const maxProbability = topPrediction[1] || 0;

  if (riskFlag.triggered) {
    return {
      sessionId,
      completedAt: new Date().toISOString(),
      severityLevel: 'urgent',
      ...copy.resultSummaries.urgent,
      maxProbability,
      topConcernLabel,
      predictions: safePredictions,
      riskFlag: {
        ...riskFlag,
        message: copy.risk.urgent,
      },
    };
  }

  return {
    sessionId,
    completedAt: new Date().toISOString(),
    maxProbability,
    topConcernLabel,
    predictions: safePredictions,
    riskFlag: {
      ...riskFlag,
      message: copy.risk.none,
    },
    ...buildNonUrgentSummary(maxProbability, language),
  };
};

/**
 * Build a result summary from the structural-entropy engine's per-module
 * verdicts. Severity + copy reuse the existing thresholds via a verdict-derived
 * pseudo-score (met → 1.0, undetermined → 0.5, not met → 0.0), and the
 * structured verdicts are attached as `moduleVerdicts`. No urgent path: the
 * suicide safety screen is out of scope for this build, so `riskFlag` stays
 * `none`.
 */
export const buildResultSummaryFromVerdicts = (
  sessionId: string,
  results: ModuleResult[],
  language: AssessmentLanguageCode = 'en',
): AssessmentResultSummary => {
  const copy = getCopy(language);
  const {predictions, maxProbability, topConcernLabel, moduleVerdicts} =
    summariseVerdicts(results);

  return {
    sessionId,
    completedAt: new Date().toISOString(),
    maxProbability,
    topConcernLabel,
    predictions,
    moduleVerdicts,
    riskFlag: {...createEmptyRiskFlag(), message: copy.risk.none},
    ...buildNonUrgentSummary(maxProbability, language),
  };
};

/** Deterministic 32-bit seed from a session id, so a resumed consultation
 * shuffles its modules in the same order as the original run. */
export const sessionSeed = (sessionId: string): number => {
  let h = 0;
  for (let i = 0; i < sessionId.length; i += 1) {
    h = (Math.imul(31, h) + sessionId.charCodeAt(i)) | 0;
  }
  return h >>> 0;
};

export const buildProviderActionLabel = (summary: AssessmentResultSummary): string => {
  if (summary.riskFlag.triggered) return 'Urgent safety escalation';
  if (summary.severityLevel === 'extra_support') return 'Consider referral or structured support';
  if (summary.severityLevel === 'difficult_time') return 'Offer brief counselling or follow-up';
  if (summary.severityLevel === 'some_stress') return 'Suggest low-intensity support';
  return 'Provide wellbeing information';
};

const SEVERITY_RANK: Record<SeverityLevel, number> = {
  doing_okay: 0,
  some_stress: 1,
  difficult_time: 2,
  extra_support: 3,
  urgent: 4,
};

export const buildAssessmentTrend = (
  currentRecord: ProviderAssessmentRecord,
  relatedRecords: ProviderAssessmentRecord[],
): AssessmentTrend => {
  const currentCompletedAt = Date.parse(currentRecord.resultSummary.completedAt);
  const previousRecord = [...relatedRecords]
    .filter((record) => {
      if (record.sessionId === currentRecord.sessionId) return false;
      return Date.parse(record.resultSummary.completedAt) < currentCompletedAt;
    })
    .sort(
      (a, b) => Date.parse(b.resultSummary.completedAt) - Date.parse(a.resultSummary.completedAt),
    )[0];

  if (!previousRecord) {
    return {
      direction: 'first',
      label: 'Compared with previous: first assessment',
      description: 'No previous local assessment is saved for this participant yet.',
      difference: 0,
      deterioration: false,
    };
  }

  const currentSummary = currentRecord.resultSummary;
  const previousSummary = previousRecord.resultSummary;
  const difference = currentSummary.maxProbability - previousSummary.maxProbability;
  const currentRank = SEVERITY_RANK[currentSummary.severityLevel];
  const previousRank = SEVERITY_RANK[previousSummary.severityLevel];
  const previousDate = new Date(previousSummary.completedAt).toLocaleString();

  if (
    (currentSummary.riskFlag.triggered && !previousSummary.riskFlag.triggered) ||
    currentRank > previousRank ||
    difference >= 0.1
  ) {
    return {
      direction: 'higher',
      label: 'Compared with previous: higher concern',
      description: `This assessment is higher than the previous local record from ${previousDate}.`,
      difference,
      previousCompletedAt: previousSummary.completedAt,
      deterioration: true,
    };
  }

  if (
    (!currentSummary.riskFlag.triggered && previousSummary.riskFlag.triggered) ||
    currentRank < previousRank ||
    difference <= -0.1
  ) {
    return {
      direction: 'lower',
      label: 'Compared with previous: lower concern',
      description: `This assessment is lower than the previous local record from ${previousDate}.`,
      difference,
      previousCompletedAt: previousSummary.completedAt,
      deterioration: false,
    };
  }

  return {
    direction: 'similar',
    label: 'Compared with previous: similar',
    description: `This assessment is similar to the previous local record from ${previousDate}.`,
    difference,
    previousCompletedAt: previousSummary.completedAt,
    deterioration: false,
  };
};

export const PROVIDER_ACTION_LABELS: Record<ProviderActionType, string> = {
  none_today: 'No action today',
  referral_suggested: 'Referral suggested',
  follow_up_needed: 'Follow-up needed',
  urgent_escalation: 'Urgent escalation',
};

const REFERRAL_STATUS_BY_ACTION: Record<ProviderActionType, ReferralStatus> = {
  none_today: 'none',
  referral_suggested: 'suggested',
  follow_up_needed: 'follow_up',
  urgent_escalation: 'urgent_escalation',
};

export const saveActiveAssessment = async (
  snapshot: AssessmentSessionSnapshot,
): Promise<boolean> => {
  return Storage.set(
    ACTIVE_ASSESSMENT_STORAGE_KEY,
    JSON.stringify({...snapshot, updatedAt: new Date().toISOString()}),
  );
};

export const loadActiveAssessment = async (): Promise<AssessmentSessionSnapshot | null> => {
  const rawValue = await Storage.get(ACTIVE_ASSESSMENT_STORAGE_KEY);
  if (!rawValue) return null;

  try {
    return JSON.parse(rawValue) as AssessmentSessionSnapshot;
  } catch {
    return null;
  }
};

export const clearActiveAssessment = async (): Promise<boolean> => {
  return Storage.remove(ACTIVE_ASSESSMENT_STORAGE_KEY);
};

export const loadCompletedAssessments = async (): Promise<ProviderAssessmentRecord[]> => {
  const rawValue = await Storage.get(COMPLETED_ASSESSMENTS_STORAGE_KEY);
  if (!rawValue) return [];

  try {
    const parsed = JSON.parse(rawValue) as ProviderAssessmentRecord[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const saveCompletedAssessment = async (
  snapshot: AssessmentSessionSnapshot,
  resultSummary: AssessmentResultSummary,
): Promise<boolean> => {
  const completedAssessments = await loadCompletedAssessments();
  const completedRecord: ProviderAssessmentRecord = {
    ...snapshot,
    status: 'completed',
    selectedQuestion: 'notSelected',
    resultSummary,
    updatedAt: resultSummary.completedAt,
  };
  const nextAssessments = [
    completedRecord,
    ...completedAssessments.filter((assessment) => assessment.sessionId !== snapshot.sessionId),
  ].slice(0, 20);

  const didSave = await Storage.set(
    COMPLETED_ASSESSMENTS_STORAGE_KEY,
    JSON.stringify(nextAssessments),
  );

  if (didSave) {
    await clearActiveAssessment();
  }

  return didSave;
};

export const updateCompletedAssessment = async (
  sessionId: string,
  updater: (record: ProviderAssessmentRecord) => ProviderAssessmentRecord,
): Promise<ProviderAssessmentRecord | null> => {
  const completedAssessments = await loadCompletedAssessments();
  let updatedRecord: ProviderAssessmentRecord | null = null;

  const nextAssessments = completedAssessments.map((record) => {
    if (record.sessionId !== sessionId) return record;

    updatedRecord = updater(record);
    return updatedRecord;
  });

  if (!updatedRecord) return null;

  await Storage.set(COMPLETED_ASSESSMENTS_STORAGE_KEY, JSON.stringify(nextAssessments));
  return updatedRecord;
};

export const saveProviderAction = async (
  sessionId: string,
  actionType: ProviderActionType,
  note: string,
): Promise<ProviderAssessmentRecord | null> => {
  const now = new Date().toISOString();

  return updateCompletedAssessment(sessionId, (record) => {
    const actionRecord: ProviderActionRecord = {
      type: actionType,
      label: PROVIDER_ACTION_LABELS[actionType],
      actedAt: now,
      note: note.trim() || undefined,
    };
    const followUp =
      actionType === 'follow_up_needed'
        ? {
            id: `follow-up-${Date.now()}`,
            date: now,
            note: note.trim() || 'Follow-up requested from provider summary.',
            status: 'planned' as const,
          }
        : undefined;

    return {
      ...record,
      providerAction: actionRecord,
      providerNotes: note,
      referralStatus: REFERRAL_STATUS_BY_ACTION[actionType],
      followUpDate: followUp?.date || record.followUpDate,
      followUps: followUp ? [followUp, ...(record.followUps || [])] : record.followUps,
    };
  });
};
